import { CalendarDays, Camera, Check, Image as ImageIcon, Trash2, User } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormField } from '@up2d8/contracts';
import { cn } from '@up2d8/ui';
import { useUpload } from '../../../shared/use-upload';
import { labelOf } from '../../forms/field-input';
import { FieldError } from './floor-kit';

/**
 * Eén veld van een formulier in de vloermodus (VLR-3, formulieren-spec §3).
 *
 * Zelfde waarden in en uit als `FieldInput` (Stap 37): tekst, een getal als tekst, een datum, een
 * paar datums, een keuze, een lijst keuzes, een bestand-id. De server valideert tegen de
 * definitieversie; dit is invoer, geen regel.
 *
 * **Keuze**: tot vier opties een rij keuzeknoppen, vanaf vijf een keuzelijst. Vier knoppen passen
 * op een telefoon zonder dat je hoeft te zoeken; bij vijf wordt een rij knoppen een puzzel.
 */
export function FloorField({
  field,
  value,
  error,
  onChange,
  offline,
}: {
  field: FormField;
  value: unknown;
  error?: string;
  onChange: (value: unknown) => void;
  offline: boolean;
}) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const label = labelOf(field.label, i18n.language, field.key);
  const help = field.help ? labelOf(field.help, i18n.language, '') : '';
  const errorId = `${id}-error`;
  const describedBy = error ? errorId : undefined;

  const range =
    field.min !== null && field.max !== null
      ? t('appForms.between', { min: field.min, max: field.max })
      : field.min !== null
        ? t('appForms.atLeast', { min: field.min })
        : field.max !== null
          ? t('appForms.atMost', { max: field.max })
          : '';
  const hint = [help, field.type === 'number' ? range : ''].filter(Boolean).join(' ');

  const head = (
    <div className="fm-label" id={`${id}-label`}>
      <label htmlFor={id}>{label}</label>{' '}
      <span className="fm-label-opt">
        {field.required ? t('appForms.required') : t('appForms.optional')}
      </span>
      {field.prefill !== 'none' ? (
        <span className="ff-prefill">
          <User aria-hidden width={12} height={12} />
          {t('appForms.fromProfile')}
        </span>
      ) : null}
    </div>
  );

  let control: ReactNode;
  switch (field.type) {
    case 'longtext':
      control = (
        <textarea
          id={id}
          className={cn('fm-input area', error && 'error')}
          value={String(value ?? '')}
          disabled={field.locked}
          maxLength={4000}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
          data-testid={`field-${field.key}`}
        />
      );
      break;

    case 'number':
    case 'text':
      control = (
        <input
          id={id}
          className={cn('fm-input', error && 'error')}
          inputMode={field.type === 'number' ? 'decimal' : undefined}
          value={String(value ?? '')}
          disabled={field.locked}
          maxLength={field.type === 'text' ? 200 : undefined}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
          data-testid={`field-${field.key}`}
        />
      );
      break;

    case 'date':
      control = (
        <DateInput
          id={id}
          value={String(value ?? '')}
          error={Boolean(error)}
          disabled={field.locked}
          onChange={onChange}
          testId={`field-${field.key}`}
        />
      );
      break;

    case 'daterange': {
      const current = (value ?? {}) as { from?: string; to?: string };
      control = (
        <div className="ff-range">
          <div className="fm-field">
            <label className="fm-label" htmlFor={`${id}-from`}>
              {t('forms.from')}
            </label>
            <DateInput
              id={`${id}-from`}
              value={current.from ?? ''}
              error={Boolean(error)}
              onChange={(from) => onChange({ ...current, from })}
              testId={`field-${field.key}-from`}
            />
          </div>
          <div className="fm-field">
            <label className="fm-label" htmlFor={`${id}-to`}>
              {t('forms.to')}
            </label>
            <DateInput
              id={`${id}-to`}
              value={current.to ?? ''}
              error={Boolean(error)}
              onChange={(to) => onChange({ ...current, to })}
              testId={`field-${field.key}-to`}
            />
          </div>
        </div>
      );
      break;
    }

    case 'choice':
      control =
        field.options.length <= 4 ? (
          <div className="ff-choices" role="radiogroup" aria-labelledby={`${id}-label`}>
            {field.options.map((option) => {
              const on = value === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  className={cn('ff-choice', on && 'on')}
                  disabled={field.locked}
                  onClick={() => onChange(option.value)}
                  data-testid={`field-${field.key}-${option.value}`}
                >
                  {on ? <Check aria-hidden width={18} height={18} strokeWidth={2.5} /> : null}
                  {labelOf(option.label, i18n.language, option.value)}
                </button>
              );
            })}
          </div>
        ) : (
          <select
            id={id}
            className={cn('fm-input', error && 'error')}
            value={String(value ?? '')}
            disabled={field.locked}
            aria-describedby={describedBy}
            onChange={(event) => onChange(event.target.value)}
            data-testid={`field-${field.key}`}
          >
            <option value="">{t('forms.choose')}</option>
            {field.options.map((option) => (
              <option key={option.value} value={option.value}>
                {labelOf(option.label, i18n.language, option.value)}
              </option>
            ))}
          </select>
        );
      break;

    case 'multichoice': {
      const chosen = Array.isArray(value) ? (value as string[]) : [];
      control = (
        <div className="ff-checks" role="group" aria-labelledby={`${id}-label`}>
          {field.options.map((option) => {
            const on = chosen.includes(option.value);
            return (
              <label key={option.value} className="ff-check">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={on}
                  disabled={field.locked}
                  onChange={(event) =>
                    onChange(
                      event.target.checked
                        ? [...chosen, option.value]
                        : chosen.filter((item) => item !== option.value),
                    )
                  }
                  data-testid={`field-${field.key}-${option.value}`}
                />
                <span className={cn('ff-box', on && 'on')} aria-hidden>
                  {on ? <Check width={18} height={18} strokeWidth={3} /> : null}
                </span>
                {labelOf(option.label, i18n.language, option.value)}
              </label>
            );
          })}
        </div>
      );
      break;
    }

    case 'photo':
      control = (
        <SinglePhoto
          value={typeof value === 'string' ? value : null}
          offline={offline}
          onChange={onChange}
          testId={`field-${field.key}`}
        />
      );
      break;

    default:
      control = null;
  }

  return (
    <div className="fm-field">
      {head}
      {control}
      {field.type === 'multichoice' && !help ? (
        <div className="fm-hint">{t('appForms.multiHint')}</div>
      ) : null}
      {hint ? <div className="fm-hint">{hint}</div> : null}
      {error ? <FieldError id={errorId}>{error}</FieldError> : null}
    </div>
  );
}

/**
 * Een datumveld met het kalendericoon rechts, dat de datumkiezer van het toestel opent.
 *
 * Een echte `type="date"`: de kiezer van de telefoon is beter dan alles wat wij zouden nabouwen, en
 * hij spreekt de taal van het toestel.
 */
function DateInput({
  id,
  value,
  error,
  disabled,
  onChange,
  testId,
}: {
  id: string;
  value: string;
  error: boolean;
  disabled?: boolean;
  onChange: (value: string) => void;
  testId: string;
}) {
  return (
    <div className="relative">
      <input
        id={id}
        type="date"
        className={cn('fm-input', error && 'error')}
        style={{ paddingRight: 44 }}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        data-testid={testId}
      />
      <CalendarDays
        aria-hidden
        width={20}
        height={20}
        className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[var(--fm-text-soft)]"
      />
    </div>
  );
}

/** Eén foto bij een aanvraag: miniatuur, "tik om te vervangen" en "Foto weghalen" (spec F3). */
function SinglePhoto({
  value,
  offline,
  onChange,
  testId,
}: {
  value: string | null;
  offline: boolean;
  onChange: (value: unknown) => void;
  testId: string;
}) {
  const { t } = useTranslation();
  const upload = useUpload();
  const [preview, setPreview] = useState<string | null>(null);

  const input = (
    <input
      type="file"
      accept="image/*"
      className="sr-only"
      disabled={offline}
      data-testid={testId}
      onChange={async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        const url = URL.createObjectURL(file);
        const fileId = await upload.upload(file, { ownerModule: 'forms', purpose: 'form_photo' });
        if (!fileId) {
          URL.revokeObjectURL(url);
          return;
        }
        setPreview(url);
        onChange(fileId);
      }}
    />
  );

  if (upload.progress !== null) {
    return (
      <div className="ff-photo-done">
        <div
          className="cf-thumb uploading"
          role="progressbar"
          aria-valuenow={upload.progress}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          {upload.progress}%
          <div className="fm-progress">
            <span style={{ width: `${upload.progress}%` }} />
          </div>
        </div>
        <span className="fm-hint">{t('forms.uploading', { progress: upload.progress })}</span>
      </div>
    );
  }

  return (
    <>
      {value ? (
        <div className="ff-photo-done">
          <label className="cf-thumb" aria-label={t('appForms.photoReplace')}>
            {preview ? (
              <img src={preview} alt="" />
            ) : (
              <ImageIcon aria-hidden width={28} height={28} />
            )}
            {input}
          </label>
          <div className="ff-photo-actions">
            <span className="fm-title" style={{ fontSize: 15 }}>
              {t('appForms.photoDone')}
            </span>
            <button
              type="button"
              className="fm-btn small ghost"
              style={{ justifyContent: 'flex-start', paddingLeft: 0 }}
              onClick={() => {
                setPreview(null);
                onChange(null);
              }}
              data-testid={`${testId}-remove`}
            >
              <Trash2 aria-hidden width={18} height={18} />
              {t('forms.photoRemove')}
            </button>
          </div>
        </div>
      ) : (
        <label
          className={cn('cf-photobox', offline && 'disabled')}
          aria-disabled={offline || undefined}
        >
          <Camera aria-hidden width={24} height={24} />
          {t('floor.addPhoto')}
          {input}
        </label>
      )}
      {offline && !value ? <div className="fm-hint">{t('appForms.photoOffline')}</div> : null}
      {upload.error ? <FieldError>{upload.error}</FieldError> : null}
    </>
  );
}
